import { GAME_NAME, STORAGE_NAMESPACE, TUNABLES } from "./config";
import { createRun, hashString, type SeatRole } from "./core";
import type { EngineScenario } from "./dev/determinism";
import {
  AGREED_POINTS_KEY,
  pointsDeskOf,
  pushBankedPoints,
  syncSavedPoints,
} from "./front/accountPoints";
import { mountBugButton } from "./front/feedback";
import { ACCOUNT_KEY, identityFor } from "./front/identity";
import { mountLanding, nameDraw, showStartFailure } from "./front/landing";
import { mountLanguagePicker, startLanguage } from "./front/language";
import { createLocalLobby, type LobbyGame, type LobbySource, tableNote } from "./front/lobby";
import { type LobbyCards, mountLobbyCards, type StartVia } from "./front/lobbyCards";
import { generatedName, keptName, NAME_KEY } from "./front/name";
import { HELD_NAME_KEY, nameDeskOf, SESSION_KEY } from "./front/nameClaim";
import type { NameRules } from "./front/nameField";
import { landingAddress, partyOfQuery, roomAddress } from "./front/roomCode";
import { createServerLobby, lobbyServerUrl, type ServerLobby } from "./front/serverLobby";
import { rememberedMatch, rememberMatch, tabGuestId, takeNextSeat } from "./front/session";
import {
  beginSignIn,
  mountSignInChip,
  noteOf,
  outcomeOf,
  type SignInOutcome,
  type SignInReturn,
  signInHere,
  takeSignInReturn,
} from "./front/signIn";
import { language, onLanguageChange, phrases } from "./i18n";
import { eventsModeOf, eventsUrlOf, switchOf } from "./net/events";
import type { RoomMatch } from "./net/match";
import { ROOM_SIDES, ROOM_TABLES } from "./net/modes";
import { GUEST_WORD } from "./net/names";
import type { RejectReason, SeatedPlayer } from "./net/protocol";
import { joinRoom, type LinkClock } from "./net/roomClient";
import { roomCodeFrom } from "./net/roomCode";
import { signInUrlOf } from "./net/signIn";
import { linksOutside, type PlatformAdapter, selectAdapter } from "./platform";
import { type ModeName, modeOfQuery, type SeatName, seatOfQuery } from "./render/seats";
import { devKnobsFor, modeOptionsFor, roomOfQuery, seedFor } from "./runSetup";
import type { Bank } from "./save/schema";
import { createRecorder, type Recorder } from "./telemetry/beacon";
import type { TelemetryEvent } from "./telemetry/bus";
import { type Landing, landingOf, teamOf } from "./telemetry/landing";
import { createVisit, type Visit } from "./telemetry/visit";

/** Dev knobs and the mock lobby exist only in dev and e2e builds; computed here, never imported (see `main.ts`). */
const IS_DEV_BUILD = import.meta.env.DEV || import.meta.env.VITE_DEBUG_HOOK === "1";

/** Spreads the indices of one page's duels over the codes: a 32-bit odd multiplier. */
const CODE_STRIDE = 2654435761;

/** Why a room turned the visitor away, on the card. */
function refusalOf(reason: RejectReason): string {
  const { refusals, notSeated } = phrases().front.cards;
  return reason === "full" || reason === "started" || reason === "unknown"
    ? refusals[reason]
    : notSeated;
}

/** One unsigned 32-bit draw from crypto: the page's duels, not the run, so not the run's PRNG. */
function draw32(): number {
  const values = new Uint32Array(1);
  crypto.getRandomValues(values);
  return values[0] ?? 0;
}

/**
 * FP1: whether the front page plays its battle: on in every build but the e2e one
 * (`VITE_SHOWCASE=off`), `?showcase=on` or `?showcase=off` asking either way.
 */
function showcaseOn(params: URLSearchParams): boolean {
  const asked = params.get("showcase");
  return asked === "on" || (asked !== "off" && import.meta.env.VITE_SHOWCASE !== "off");
}

/** The socket to a duel's room on the lobby server. */
function roomSocket(serverUrl: string, code: string): () => WebSocket {
  return () => new WebSocket(`${serverUrl}/room/${code}`);
}

/** The build's commit, written into `index.html` by the build; "dev" on the dev server. */
function buildOf(): string {
  return document.querySelector<HTMLMetaElement>('meta[name="build"]')?.content || "dev";
}

/**
 * B3.9: the page's recorder of the playtest's events, or none: no lobby server, or the events are
 * off for this build and address (`eventsModeOf`; the playtest page records every visit since
 * the owner approved the privacy note, D-066). What it holds goes out when the page is
 * hidden or closed; an error no code caught is recorded too. AN1: with a visit, the page's close
 * is recorded first (`page_left`), and the visit counts the time the page is in view.
 */
function recorderFor(
  serverUrl: string | undefined,
  guest: string,
  visit: Visit | undefined,
): Recorder | undefined {
  const mode = eventsModeOf(location.search, import.meta.env.VITE_EVENTS, IS_DEV_BUILD);
  if (serverUrl === undefined || mode === "off") {
    return undefined;
  }
  const recorder = createRecorder({
    url: eventsUrlOf(serverUrl),
    guest,
    session: crypto.randomUUID(),
    build: buildOf(),
    now: () => performance.now(),
    // text/plain needs no preflight; the beacon outlives a closed page.
    send: (url, body) => navigator.sendBeacon(url, new Blob([body], { type: "text/plain" })),
    every: (ms, task) => {
      setInterval(task, ms);
    },
    ...(visit === undefined ? {} : { observe: visit.observe }),
  });
  document.addEventListener("visibilitychange", () => {
    visit?.shown(document.visibilityState === "visible", performance.now());
    if (document.visibilityState === "hidden") {
      recorder.flush();
    }
  });
  window.addEventListener("pagehide", () => {
    if (visit) {
      recorder.record(visit.left(performance.now()));
    }
    recorder.flush();
  });
  window.addEventListener("error", (event) =>
    recorder.record({ type: "error_uncaught", message: String(event.message) }),
  );
  window.addEventListener("unhandledrejection", (event) =>
    recorder.record({
      type: "error_uncaught",
      message: String(event.reason instanceof Error ? event.reason.message : event.reason),
    }),
  );
  return recorder;
}

/**
 * B3.10.1: what the Worker made of Google's token (`/signin`); a signed-in account is kept in the
 * lasting store until Sign out. The page waits `answerTimeoutMs` at most, then stays a guest.
 * NM2: the session is kept beside it, and the name the account holds becomes this page's.
 */
async function finishSignIn(
  serverUrl: string,
  returned: SignInReturn,
  store: PlatformAdapter["data"],
  namesOn: boolean,
): Promise<SignInOutcome> {
  const outcome = await outcomeOf(returned, async (body) => {
    const response = await fetch(signInUrlOf(serverUrl), {
      method: "POST",
      // text/plain needs no preflight.
      headers: { "Content-Type": "text/plain" },
      body,
      signal: AbortSignal.timeout(TUNABLES.signIn.answerTimeoutMs),
    });
    return response.text();
  });
  if (outcome.result === "ok") {
    store.set(ACCOUNT_KEY, outcome.account);
    if (!namesOn) {
      return outcome;
    }
    if (outcome.session !== undefined) {
      store.set(SESSION_KEY, outcome.session);
    }
    if (outcome.name !== undefined) {
      store.set(HELD_NAME_KEY, outcome.name);
      store.set(NAME_KEY, outcome.name);
    } else {
      store.remove(HELD_NAME_KEY);
    }
  }
  return outcome;
}

/**
 * NM2: Sign out, and a session the Worker no longer takes: the account, its session and its name
 * leave this browser, and the field gets a new name (the old one stays the account's). AP1: the
 * agreement on the bank goes too; the save keeps its bank, this browser's from now on.
 */
function signOut(store: PlatformAdapter["data"]): void {
  const held = store.get(HELD_NAME_KEY);
  for (const key of [ACCOUNT_KEY, SESSION_KEY, HELD_NAME_KEY, AGREED_POINTS_KEY]) {
    store.remove(key);
  }
  if (held !== null && store.get(NAME_KEY) === held) {
    store.set(NAME_KEY, generatedName(nameDraw(), language()));
  }
  location.reload();
}

/**
 * NM2, AP1: a post to the Worker as text (no preflight), its answer's text whatever its status,
 * given up after `timeoutMs`.
 */
function poster(timeoutMs: number): (url: string, body: string) => Promise<string> {
  return async (url, body) => {
    const response = await fetch(url, {
      method: "POST",
      headers: { "Content-Type": "text/plain" },
      body,
      signal: AbortSignal.timeout(timeoutMs),
    });
    return response.text();
  };
}

/** B3.10.1: a sign-in event, alone in its batch: an older Worker refuses a batch that holds one. */
function recordAlone(recorder: Recorder | undefined, event: TelemetryEvent): void {
  recorder?.flush();
  recorder?.record(event);
  recorder?.flush();
}

/**
 * B3.9: the front page's first contentful paint and the moment Play now worked, once painted.
 * AN1: with the visits switch, where the visitor came from and whether the browser is the team's.
 */
function tellLanded(recorder: Recorder, arrival: Partial<Landing> & { team?: boolean }): void {
  const ready = performance.getEntriesByName("play-ready")[0]?.startTime ?? performance.now();
  new PerformanceObserver((list, observer) => {
    const paint = list.getEntriesByName("first-contentful-paint")[0];
    if (paint) {
      observer.disconnect();
      recorder.record({
        type: "page_landed",
        paint_ms: Math.round(paint.startTime),
        play_ready_ms: Math.round(ready),
        ...arrival,
      });
    }
  }).observe({ type: "paint", buffered: true });
}

/**
 * The page's entry (B3.1, B3.2). The front page is in `index.html` and paints before this runs;
 * here it gets its name, the day's map, a working Play now and the duels' cards, and the game
 * (`main.ts`, with Pixi) is fetched by `import()` while the visitor looks. An address that names
 * a seat (`?seat=king`, the e2e tests) or a duel's room (`?room=K7Q2M`, a link to a duel) skips
 * the page and starts the game at once. With a lobby server (B3.5) the cards are its duels and a
 * Join takes a seat at the duel's table; the game starts when the room says so, in the seat it
 * gave, and plays the room's turns (B3.6). A reload of a room match this tab sits in rejoins it
 * and replays its turns; a room it cannot reach, or that no longer knows it, gives the duel
 * against scripts instead: the kill switch (B3.7).
 */
async function start(): Promise<void> {
  document.title = GAME_NAME;
  // B3.10.1: Google's answer leaves the address before anything reads it.
  const signInReturn = takeSignInReturn();
  if (IS_DEV_BUILD) {
    // B3.4: the engines' check reads the core's hashes from the page, with no game booted (no WebGL).
    Object.assign(globalThis, {
      __engines: (scenario: EngineScenario) =>
        import("./dev/determinism").then((module) => module.engineScenario(scenario)),
      // B3.7: the time to replay a whole 8-minute duel, as a rejoining page does.
      __replay: () =>
        import("./dev/determinism").then((module) => module.replayBench(() => performance.now())),
    });
  }
  const adapter = selectAdapter();
  await adapter.init();
  // L10N1: the language first, so every word written from here on is in it.
  startLanguage(adapter.data);
  const params = new URLSearchParams(location.search);
  /** G44: the seat a room match's Change seat picked, taken by whichever page loads next. */
  const nextSeat = takeNextSeat();
  /** The visitor at the lobby server: this tab's, kept through its reloads (B3.7). */
  const playerId = tabGuestId(() => crypto.randomUUID());
  const serverUrl = lobbyServerUrl(location.search, import.meta.env.VITE_LOBBY_URL, IS_DEV_BUILD);
  // B3.10.1: Google sign-in on our own URL, a switch of its own, off for visitors until the owner
  // approves the note that describes it (N38, D-077).
  const signInOn = signInHere({
    mode: switchOf("signin", location.search, import.meta.env.VITE_SIGNIN, IS_DEV_BUILD),
    framed: window.top !== window.self,
    origin: location.origin,
    platform: import.meta.env.VITE_PLATFORM ?? "local",
    server: serverUrl !== undefined,
  });
  // NM2: names owned by accounts, a switch of their own on top of sign-in's, on for visitors since
  // the owner approved the note that describes them (N47, D-111). An account signed in before it
  // has no session: its name is a guest's until it signs in again.
  const namesOn =
    signInOn &&
    serverUrl !== undefined &&
    switchOf("names", location.search, import.meta.env.VITE_NAMES, IS_DEV_BUILD) !== "off";
  const signInDone =
    signInOn && signInReturn && serverUrl !== undefined
      ? await finishSignIn(serverUrl, signInReturn, adapter.data, namesOn)
      : undefined;
  const session = namesOn ? (adapter.data.get(SESSION_KEY) ?? undefined) : undefined;
  /** NM2: a join shows the session, so the room seats the account's own name under it. */
  const sealed = session === undefined ? {} : { session };
  // B3.10: the kept guest and the feedback are switches of their own, on since the owner approved
  // the note that describes them (N35, D-071); the rooms keep the tab's guest.
  const identity = identityFor({
    kept:
      switchOf("keptguest", location.search, import.meta.env.VITE_KEPT_GUEST, IS_DEV_BUILD) !==
      "off",
    tabId: playerId,
    store: adapter.data,
    fresh: () => crypto.randomUUID(),
    account: signInOn ? (adapter.data.get(ACCOUNT_KEY) ?? undefined) : undefined,
  });
  // AN1: where a visit came from and where it ended, a switch of its own on top of the events',
  // on for visitors since the owner approved the note that describes it (N51, D-124).
  const visits =
    switchOf("visits", location.search, import.meta.env.VITE_VISITS, IS_DEV_BUILD) !== "off";
  const team = visits && teamOf(location.search, adapter.data);
  const recorder = recorderFor(
    serverUrl,
    identity.id,
    visits ? createVisit({ visible: document.visibilityState === "visible", team }) : undefined,
  );
  // AP1: what the account has earned kept with it, a switch of its own on top of names', on for
  // visitors since the owner approved the note that describes it (N48, D-115).
  // The save's bank meets the account's before the game reads it; each run's end sends it on.
  const pointsOn =
    namesOn &&
    switchOf("points", location.search, import.meta.env.VITE_POINTS, IS_DEV_BUILD) !== "off";
  const pointsDesk =
    pointsOn && serverUrl !== undefined && session !== undefined && identity.kind === "google"
      ? pointsDeskOf({ serverUrl, session, post: poster(TUNABLES.points.answerTimeoutMs) })
      : undefined;
  const pointsSynced: Promise<unknown> = pointsDesk
    ? syncSavedPoints({
        store: adapter.data,
        account: identity.id,
        desk: pointsDesk,
        nowMs: Date.now(),
      })
    : Promise.resolve();
  const onBanked = pointsDesk
    ? (bank: Bank) => {
        void pushBankedPoints({
          store: adapter.data,
          account: identity.id,
          desk: pointsDesk,
          bank,
        });
      }
    : undefined;
  if (signInDone) {
    recordAlone(recorder, {
      type: "signin_done",
      result: signInDone.result,
      ...(signInDone.result === "refused" ? { reason: signInDone.reason } : {}),
    });
  }
  const feedback =
    recorder !== undefined &&
    switchOf("feedback", location.search, import.meta.env.VITE_FEEDBACK, IS_DEV_BUILD) !== "off";
  if (feedback && recorder) {
    mountBugButton({
      send: (text, context) => {
        // What is held goes first: a server that refuses the report refuses only the report.
        recorder.flush();
        recorder.record({ type: "bug_reported", text, screen: context.screen, tick: context.tick });
        recorder.flush();
      },
    });
  }
  if (IS_DEV_BUILD && recorder) {
    // B3.9: the e2e test sends what is held at once and counts what went.
    Object.assign(globalThis, { __events: recorder });
  }
  // B3.9: a page that records links the privacy note; one that records nothing links nothing.
  const privacyLink = document.getElementById("privacy-link");
  if (privacyLink) {
    privacyLink.hidden = recorder === undefined;
  }
  // D-091: our own page links the itch.io page, D-134 the Patreon page; a portal's frame neither.
  const outside = linksOutside();
  for (const id of ["itch-link", "patreon-link"]) {
    const link = document.getElementById(id);
    if (link) {
      link.hidden = !outside;
    }
  }
  const linkClock: LinkClock = {
    now: () => performance.now(),
    after: (ms, task) => {
      setTimeout(task, ms);
    },
  };
  /** A room match this tab sat in, joined again: its turns replayed, or undefined if it is gone. */
  const rejoin = (url: string, code: string): Promise<RoomMatch | undefined> =>
    new Promise((resolve) => {
      const gone = () => {
        rememberMatch(undefined);
        resolve(undefined);
      };
      joinRoom({
        open: roomSocket(url, code),
        join: {
          type: "join",
          playerId,
          name: adapter.data.get(`${STORAGE_NAMESPACE}:name`) || GUEST_WORD,
          ...sealed,
        },
        clock: linkClock,
        events: {
          seated: () => undefined,
          table: () => undefined,
          start: (_seat, _seedText, room) => resolve(room),
          rejected: gone,
          lost: gone,
        },
      });
    });

  /**
   * K4: a room's link opened in a tab that never sat at its table asks to sit down in the match
   * under way, in a seat nobody holds (the address's seat and party asked for). A room still
   * waiting for its start sends the visitor to the front page, where its card is; a room gone,
   * full or out of reach leaves the link to play the room's map against scripts, as before.
   */
  const dropIn = (url: string, code: string): Promise<RoomMatch | undefined> =>
    new Promise((resolve) => {
      const party = partyOfQuery(location.search);
      joinRoom({
        open: roomSocket(url, code),
        join: {
          type: "join",
          playerId,
          name: keptName(
            adapter.data,
            nameDraw,
            language(),
            adapter.data.get(HELD_NAME_KEY) ?? undefined,
          ),
          ...sealed,
          wants: seatOfQuery(location.search) ?? "king",
          late: true,
          ...(party === undefined ? {} : { party }),
        },
        clock: linkClock,
        events: {
          seated: () => undefined,
          table: () => undefined,
          start: (_seat, _seedText, joined) => {
            rememberMatch(code);
            resolve(joined);
          },
          rejected: (reason) => {
            if (reason === "open") {
              location.replace(landingAddress(location.search));
              return;
            }
            resolve(undefined);
          },
          lost: () => resolve(undefined),
        },
      });
    });

  const room = roomOfQuery(location.search);
  if (room !== undefined && seatOfQuery(location.search) === undefined) {
    // A link to a duel is the King's seat in it, like a Join on its card.
    history.replaceState(
      null,
      "",
      roomAddress(
        location.search,
        room,
        modeOfQuery(location.search) === "company" ? "company" : "duel",
      ),
    );
  }
  if (seatOfQuery(location.search) !== undefined) {
    document.getElementById("landing")?.remove();
    // The game's code first: on a slow machine its parse would hold the page past the room's
    // answer, and the link would count the room unreachable (`connectTimeoutMs`, K4's CI run 220).
    const game = await import("./main");
    const match =
      room === undefined || serverUrl === undefined
        ? undefined
        : rememberedMatch() === room
          ? await rejoin(serverUrl, room)
          : await dropIn(serverUrl, room);
    await pointsSynced;
    await game.startGame(adapter, match, recorder, feedback, onBanked);
    return;
  }

  const knobs = devKnobsFor(location.search, IS_DEV_BUILD);
  // K1: the company duel's link shows its own, bigger map; K2: so does its card.
  const runFor = (seedText: string, mode: ModeName) =>
    createRun(hashString(seedText), modeOptionsFor(knobs, mode, "king"));
  const { seedText, daily } = seedFor(location.search, new Date());
  const names: NameRules | undefined =
    namesOn && serverUrl !== undefined
      ? {
          desk: nameDeskOf({ serverUrl, session, post: poster(TUNABLES.names.answerTimeoutMs) }),
          signedIn: identity.kind === "google" && session !== undefined,
          held: adapter.data.get(HELD_NAME_KEY) ?? undefined,
          keep: (held) => adapter.data.set(HELD_NAME_KEY, held),
          expired: () => signOut(adapter.data),
        }
      : undefined;
  const mockLobby = IS_DEV_BUILD && params.get("lobby") === "mock";
  const landing = mountLanding({
    adapter,
    run: runFor(seedText, modeOfQuery(location.search) ?? "duel"),
    caption: () =>
      daily ? phrases().front.todaysMap(seedText) : phrases().front.namedMap(seedText),
    ...(names === undefined ? {} : { names }),
    waitsForLobby: serverUrl !== undefined && !mockLobby,
    daily,
    ...(nextSeat === undefined ? {} : { seat: nextSeat }),
    // D-203: Play now's cards are the start screen now, as the game's own were (5.18).
    chosen: (seat) => recorder?.record({ type: "seat_chosen", seat, from: "start" }),
    // FP1: the battle behind the page's words, drawn by the game's chunk once it is fetched.
    ...(showcaseOn(params)
      ? {
          battle: (box, run, onScore, wanted) =>
            import("./main").then((module) =>
              wanted() ? module.startShowcase(box, run, onScore) : undefined,
            ),
        }
      : {}),
  });
  // D-120: a lobby server slower than this is shown late, its map and duels in place of ours.
  setTimeout(() => landing.settle(), TUNABLES.landing.lobbyWaitMs);
  document
    .querySelector("#landing .title-row")
    ?.append(mountLanguagePicker({ id: "language", store: adapter.data }));
  if (signInOn) {
    const signedIn = identity.kind === "google";
    const showChip = () =>
      mountSignInChip({
        account: signedIn ? identity.id : undefined,
        note: noteOf(signInDone),
        signIn: beginSignIn,
        signOut: () => signOut(adapter.data),
      });
    showChip();
    onLanguageChange(showChip);
    if (!signedIn) {
      recordAlone(recorder, { type: "signin_shown" });
    }
  }
  const game = import("./main");
  // A fetch that fails before the click is reported by the click, not as an unhandled rejection.
  game.catch(() => undefined);
  landing.onPlay(async (seat) => {
    // D-108: with the lobby server, Play now is the company card's Start now (`playOnline`), in
    // the seat picked (D-203).
    if (await playOnline(seat)) {
      return;
    }
    await pointsSynced;
    // FP1: the front page's battle gives back its GPU and its ticks before the game draws.
    landing.quiet();
    await (await game).startGame(adapter, undefined, recorder, feedback, onBanked, seat);
    landing.close();
  });
  if (recorder) {
    tellLanded(
      recorder,
      visits
        ? {
            ...landingOf({
              host: location.hostname,
              referrer: document.referrer,
              search: location.search,
            }),
            ...(team ? { team } : {}),
          }
        : {},
    );
  }

  const salt = draw32();
  const codeOf = (index: number): string =>
    roomCodeFrom((salt + Math.imul(index, CODE_STRIDE)) >>> 0);
  const local = createLocalLobby(codeOf);
  let server: ServerLobby | undefined;
  let source: LobbySource = local;
  if (mockLobby) {
    source = (await import("./front/mockLobby")).mockLobbyFromAddress(
      location.search,
      Date.now(),
      codeOf,
    );
  } else if (serverUrl !== undefined) {
    server = createServerLobby({
      open: () => new WebSocket(`${serverUrl}/lobby`),
      hello: {
        type: "hello",
        playerId,
        name: landing.name(),
        build: buildOf(),
      },
      fallback: local,
      now: () => Date.now(),
      after: (ms, task) => setTimeout(task, ms),
    });
    source = server;
  }

  /**
   * Into the game on a game's seed: the seat asked for against scripts, or a room's match (B3.6),
   * whose mode is the room's (K2).
   */
  const enterDuel = async (
    listed: LobbyGame,
    wants: SeatRole,
    room?: RoomMatch,
    party?: string,
  ): Promise<void> => {
    const code = listed.id;
    const loaded = await game;
    const mode = room?.mode ?? listed.mode;
    const seat = room ? (ROOM_TABLES[mode][room.match.seat]?.role ?? wants) : wants;
    history.replaceState(null, "", roomAddress(location.search, code, mode, seat, party));
    // B3.7: a reload of this tab comes back to the room match, not to a duel against scripts.
    rememberMatch(room ? code : undefined);
    await pointsSynced;
    landing.quiet();
    await loaded.startGame(adapter, room, recorder, feedback, onBanked);
    cards.close();
    server?.close();
    landing.close();
  };

  /**
   * K2: a party's word from the address (`?party=`): friends with one sit in one kingdom. Since K3
   * it fills the company card's field, whose word goes with that card's Join.
   */
  const party = partyOfQuery(location.search);

  /** The game whose table the visitor sits at, before it starts: one at a time. */
  let seatedAt: string | undefined;
  /** K4.1: that table's Start now, while the room takes an ask; D-108: on behalf of `via`. */
  let startNow: ((via?: StartVia) => void) | undefined;
  /**
   * `quick` (D-105): the seat was taken by Start now, which is asked for as soon as it is given.
   * `via` (D-108): Play now waits on this seat: it enters the game when the room starts it, and
   * hears when no seat comes. False when the visitor already sits at a table.
   */
  const sitAt = (
    duel: LobbyGame,
    button: HTMLButtonElement,
    wants: SeatRole,
    url: string,
    sitsWith: string | undefined,
    quick: boolean,
    via: StartVia | undefined,
  ): boolean => {
    if (seatedAt !== undefined) {
      return false;
    }
    let waiting = via;
    seatedAt = duel.id;
    const label = button.textContent ?? phrases().front.cards.join;
    button.disabled = true;
    button.textContent = phrases().front.cards.seatedButton;
    recorder?.setGame(duel.id);
    /** B3.9: when the room gave the seat, for the time waited at the table. */
    let seatedSinceMs: number | undefined;
    /** The table is left: the time waited goes to the journal and the Join button is free again. */
    const left = (reason: "refused" | "lost") => {
      if (seatedSinceMs !== undefined) {
        const waited = Math.round(performance.now() - seatedSinceMs);
        recorder?.record({ type: "lobby_left", waited_ms: waited, reason });
      }
      seatedAt = undefined;
      startNow = undefined;
      button.disabled = false;
      button.textContent = label;
      waiting?.left();
      waiting = undefined;
    };
    /**
     * K4.1: the table as the room last told it, how many at it asked to start now (undefined: the
     * room takes no ask), whether this visitor asked, and whether the ask went to the room.
     */
    let players: readonly SeatedPlayer[] = [];
    let ready: number | undefined;
    let asked = quick;
    let sent = false;
    const ask = () => {
      if (asked && !sent && ready !== undefined) {
        sent = true;
        link.ready();
      }
    };
    const showTable = () =>
      cards.note(duel.id, {
        text: () => tableNote(players, ready, ROOM_SIDES[duel.mode]),
        seated: true,
        ...(ready === undefined ? {} : { startNow: asked ? "asked" : "offer" }),
      });
    const link = joinRoom({
      open: roomSocket(url, duel.id),
      join: {
        type: "join",
        playerId,
        name: landing.name(),
        wants,
        ...sealed,
        ...(sitsWith === undefined ? {} : { party: sitsWith }),
      },
      clock: linkClock,
      events: {
        seated: (_seat, table, _mode, count) => {
          if (seatedSinceMs === undefined) {
            seatedSinceMs = performance.now();
            recorder?.record({ type: "lobby_joined" });
          }
          players = table;
          ready = count;
          ask();
          showTable();
        },
        table: (table, count) => {
          players = table;
          ready = count;
          ask();
          showTable();
        },
        start: (_seat, _seedText, room) => {
          const enter = () => enterDuel(duel, wants, room, sitsWith);
          if (waiting) {
            waiting.enter(enter);
          } else {
            landing.enter(button, enter);
          }
        },
        rejected: (reason) => {
          left("refused");
          cards.note(duel.id, { text: () => refusalOf(reason), seated: false });
        },
        lost: () => {
          left("lost");
          cards.note(duel.id, undefined);
        },
      },
    });
    startNow = (asker) => {
      waiting = asker ?? waiting;
      if (ready !== undefined) {
        asked = true;
        ask();
        showTable();
      }
    };
    if (quick) {
      // Pressed at once, before the room answers: the button reads Ready while the seat comes.
      cards.note(duel.id, { text: () => "", seated: true, startNow: "asked" });
    }
    return true;
  };

  /** A seat taken from a card: at the server's table, or with no server straight into the game. */
  const take = (
    duel: LobbyGame,
    button: HTMLButtonElement,
    wants: SeatRole,
    sitsWith: string | undefined,
    quick: boolean,
    via?: StartVia,
  ): void => {
    if (server?.live && serverUrl !== undefined) {
      if (!sitAt(duel, button, wants, serverUrl, sitsWith, quick, via)) {
        via?.left();
      }
      return;
    }
    if (via) {
      via.left();
      return;
    }
    landing.enter(button, () => enterDuel(duel, wants));
  };

  const cards: LobbyCards = mountLobbyCards({
    source,
    runFor,
    now: () => Date.now(),
    ...(party === undefined ? {} : { party }),
    // D-105: the company card's Start now from the start seats a visitor who sits nowhere as King
    // (G44: Play now for a Change seat's pick, in that seat).
    onStartNow: (duel, button, wants, sitsWith, via) => {
      if (seatedAt === duel.id) {
        startNow?.(via);
        return;
      }
      take(duel, button, wants, sitsWith, true, via);
    },
    onJoin: (duel, button, wants, sitsWith) => take(duel, button, wants, sitsWith, false),
    // D-108: the front page shows the map and the note of the company table Play now sits at.
    onPlayable: (listed) =>
      landing.online(listed && { run: runFor(listed.seedText, listed.mode), code: listed.id }),
    onSettled: () => {
      landing.settle();
      if (nextSeat) {
        // G44: a Change seat's pick plays at once, as Play now would; after the cards are mounted.
        setTimeout(() => landing.press(), 0);
      }
    },
    // FP1: the wait at the visitor's table, over the battle and on Play now.
    onCountdown: (remainingMs) => landing.countdown(remainingMs),
  });

  /**
   * D-108: Play now online: the company card's Start now pressed on Play now's behalf, so the
   * visitor sits in the seat Play now's cards picked (D-203; G44: a Change seat's pick) and the
   * company starts in 3 s. True once the room started it and the game is on screen; false with no
   * lobby server, no company card or no seat (refused, lost): Play now then plays the duel against
   * scripts, as before.
   */
  const playOnline = (seat: SeatName): Promise<boolean> =>
    new Promise((resolve, reject) => {
      const pressed =
        server?.live === true &&
        cards.startNow(
          "company",
          {
            enter: (start) => {
              start().then(() => resolve(true), reject);
            },
            left: () => resolve(false),
          },
          seat,
        );
      if (!pressed) {
        resolve(false);
      }
    });
}

start().catch((error: unknown) => {
  console.error("boot failed", error);
  showStartFailure();
});
